import { ChoiceLayout, CustomFieldType, IntakeFormSections } from '@ee/common/enums';
import { EVICTION_REASONS_CLAIMS_MAP } from '@ee/common/constants';
import {
  IntakeFormSection,
  LAYOUT_COLUMNS,
  LayoutItem,
  LayoutRow,
  migrateFieldsToRows,
  CompositeType,
  IntakeOption,
  PlacementLockReason
} from '@ee/common/models';
import { LayoutSectionView, isIntakePlaceable, lockReasonFor } from './intake-layout.model';
import { COMPOSITE_LABELS, COMPOSITE_PART_KEYS } from './composites';
import { dedupeByKey, repackOverfullRows } from './layout-row-pack';

/**
 * The product-owned questions of the public intake form, and the projection that keeps a firm's
 * arrangement of them without letting a saved copy of a label or a type go stale.
 *
 * These questions are not stored as field definitions. The declaration is what the wizard asks.
 */

/**
 * Page one of the public form, which is **not** stored anywhere.
 *
 * Its questions live in two hand-written components — `user-form.component.ts` and
 * `organization-form.component.ts` — not in `IntakeFormSection.fields`. Deriving the page from the data
 * therefore produced an empty Business Information and a Contact Information holding nothing but a
 * stray custom field that the v1 renderer never shows either. Declared here so the canvas states what
 * the form actually asks.
 *
 * Keep in step with those two components. The alternative — reading the fields out of the markup — is
 * not available, and inventing them from the section map is what went wrong the first time.
 */
const PUBLIC_FORM_NOTE =
  'These questions create the client\'s account, so they are fixed. You can still add a heading or ' +
  'help text around them.';

/**
 * v1's `options.claims.type`, read once at projection and never again.
 *
 * `simple` drew one Total Due and ignored every itemised option; anything else drew the itemised
 * amounts. v2 has no mode: a form asks one Total Owed by placing nothing else, or places the amounts
 * and the total is worked out from them. So a `simple` form starts with only the total placed and
 * the amounts offered in Add field, which is the same translation `show: false` gets.
 */
function claimsAskedAsOneTotal(options: Record<string, IntakeOption> | null | undefined): boolean {
  return options?.['claims']?.type === 'simple';
}

interface DeclaredCell {
  key: string;
  label: string;
  span: number;
  /** Required when v1 has no option to say otherwise. An option's `required` wins where there is one. */
  required?: boolean;
  /** So the preview draws the control the wizard draws, rather than a generic box. */
  type?: CustomFieldType;
  /** Choices for a select, where the wizard has a fixed list. */
  options?: string[];
  /** How a radio lines up its choices when the placement does not say. The field's default, not the form's. */
  choiceLayout?: ChoiceLayout;
  /**
   * The `IntakeOption` that gates this field in v1, when one does. Defaults to the field key.
   *
   * v2 has no `show`: a field is placed or it is not. So the option is read once, at projection, and
   * translated — `show: false` becomes *absent from the rows and offered in Add field*, and `required`
   * becomes the placement's own flag. After that the option is vestigial, the way `claims_preset` was.
   */
  option?: string;
  /** No option gates it in v1; the wizard always renders it. */
  always?: boolean;
  /**
   * What an *absent* option means. The tenant step shows a field whose option was never written; the
   * claims and lease-terms steps hide one (`option?.show &&`), so their cells say so.
   */
  defaultShow?: boolean;
  /**
   * An option whose `show` hides this cell. v1 asks Rent Owed as one amount *or* as a month-by-month
   * history, never both: the history replaces the amount and writes its sum into it.
   */
  hiddenBy?: string;
  /** An itemised claims amount: not placed for a form v1 asked as one Total Due — see `claimsAskedAsOneTotal`. */
  itemised?: boolean;
  /** Drawn with a `$` and checked as a dollar amount, as v1's `Validators.pattern(CURRENCY)` did. */
  monetary?: boolean;
  include_in_total?: boolean;
  subtract_from_total?: boolean;
  /** Must be greater than zero — see `FieldDefinitionView.positive`. */
  positive?: boolean;
  /**
   * Required whatever the option says. v1's claims editor wrote `required: false` on the monthly
   * history when Simple was chosen and never put it back, while the step itself always demanded one
   * completed month; the stale flag must not make the v2 question optional on migration.
   */
  fixedRequired?: boolean;
  /** The product's own hint, under a firm's `localization` / option hint where one is set. */
  hint?: string;
  /** What a new answer starts as — see `FieldDefinitionView.initial`. */
  initial?: unknown;

  /** Holds its section's total — see `FieldDefinitionView.section_total`. */
  section_total?: boolean;
  /** Cannot be removed or moved. The section is still editable around it. */
  locked?: boolean;
  /**
   * Placed as one block whose parts are never individually placeable.
   *
   * The `key` is then the block's own key rather than a field's — nothing writes to it — and the parts
   * in [COMPOSITE_PARTS] are what the wizard renders and stores.
   */
  composite?: CompositeType;
}

interface DeclaredSection {
  key: IntakeFormSections;
  title: string;
  rows: DeclaredCell[][];
  /** The sentence shown on the section. */
  note: string;
  /**
   * Whether a firm can add and remove these placements.
   *
   * False for the account pages, whose questions create the client's login and cannot be anything else.
   * True for the tenant step, where v1's `show` toggles were exactly this decision wearing a different
   * hat — so the toggles become presence, and the Add field picker is where a switched-off one comes
   * back from.
   */
  editable?: boolean;
  /**
   * Whether the section also holds fields that are not declared here.
   *
   * The account pages are wholly the product's, so anything stored against them that the declaration
   * does not know is a stale write and is dropped. Property is not like that: the wizard owns the
   * address block and the two questions above it, but the section is also where a firm's own property
   * fields live — a gate code, an APN, a property ID. Merging keeps them; replacing would delete them
   * off the form on first read.
   */
  merge?: boolean;
}

export const PUBLIC_FORM_SECTIONS: DeclaredSection[] = [
  {
    key: IntakeFormSections.USER,
    title: 'Contact Information',
    note: PUBLIC_FORM_NOTE,
    rows: [
      [
        { key: 'first_name', label: 'First Name', span: 6, required: true, type: CustomFieldType.TEXT },
        { key: 'last_name', label: 'Last Name', span: 6, required: true, type: CustomFieldType.TEXT }
      ],
      [
        { key: 'email', label: 'Email Address', span: 6, required: true, type: CustomFieldType.TEXT },
        { key: 'phone_number', label: 'Phone Number', span: 6, required: true, type: CustomFieldType.TEXT }
      ]
    ]
  },
  {
    key: IntakeFormSections.ORGANIZATION,
    title: 'Business Information',
    note: PUBLIC_FORM_NOTE,
    rows: [
      // The filer's own company, on the page that creates their account. The *plaintiff* is a separate
      // field on page two — see the PLAINTIFF section below.
      [{ key: 'company_name', label: 'Company Name', span: 12, required: true, type: CustomFieldType.TEXT }],
      [{ key: 'street_address_1', label: 'Street Address', span: 12, required: true }],
      [{ key: 'street_address_2', label: 'Street Address 2', span: 12 }],
      [
        { key: 'city', label: 'City', span: 6, required: true },
        { key: 'state', label: 'State', span: 3, required: true, type: CustomFieldType.SELECT },
        { key: 'zip_code', label: 'Zip Code', span: 3, required: true }
      ]
    ]
  }
  ,
  {
    /*
     * The plaintiff.
     *
     * Keyed `plaintiff`, which is what every document template merges and what
     * `FillableFormFieldBuilder` resolves — from `eviction.companyName`. The built-in field annotation
     * on `Eviction` says `company_name` for the same thing, so the intake key and the merge field
     * disagree in the codebase; `plaintiff` is the one that is addressable from a template, so it is
     * the one the layout uses.
     *
     * Not to be confused with page one's `company_name`, which is the *filer's* own company on the
     * account-creation page.
     */
    key: IntakeFormSections.PLAINTIFF,
    title: 'Plaintiff',
    note: 'Who is filing. Merged into documents as #plaintiff#.',
    editable: true,
    rows: [
      [
        {
          key: 'plaintiff',
          label: 'Plaintiff Name',
          span: LAYOUT_COLUMNS,
          required: true,
          always: true,
          locked: true,
          type: CustomFieldType.TEXT
        }
      ]
    ]
  },
  {
    /*
     * The tenant party, and nothing else.
     *
     * `eviction-tenants-section.component.ts` has **no field loop at all** — every control in it is
     * hard-coded markup gated on an option — so the tenant accordion, the two declarations and the
     * eviction reason were all invisible to the layout. That is why the tenants never appeared on the
     * canvas: there was nothing in the data to draw.
     */
    key: IntakeFormSections.TENANT,
    title: 'Tenant Information',
    note:
      'Asked once per tenant — the wizard repeats these for each one. Add or remove them here; the ' +
      'ones your form used to switch off are in Add field.',
    editable: true,
    rows: [
      [
        {
          /*
           * Namespaced, because these are fields of a *tenant*, not of the case.
           *
           * The wizard collects them inside a per-tenant form group — the storage path is
           * `tenants[i].email` — and the account page has its own top-level `email`, `first_name`,
           * `last_name` and `phone_number` for the person filing. Sharing a key would have made the
           * registry, the Add-field picker and `placedKeys` treat the filer's email and a tenant's as
           * one field.
           */
          key: 'tenants.first_name',
          option: 'first_name',
          label: 'Tenant\'s First Name',
          span: 4,
          required: true,
          always: true,
          locked: true,
          type: CustomFieldType.TEXT
        },
        { key: 'tenants.middle_name', option: 'middle_name', label: 'Tenant\'s Middle Name', span: 4, type: CustomFieldType.TEXT },
        {
          key: 'tenants.last_name',
          option: 'last_name',
          label: 'Tenant\'s Last Name',
          span: 4,
          required: true,
          always: true,
          locked: true,
          type: CustomFieldType.TEXT
        }
      ],
      [
        { key: 'tenants.phone_number', option: 'phone_number', label: 'Phone Number', span: 4, type: CustomFieldType.TEXT },
        { key: 'tenants.email', option: 'email', label: 'Email Address', span: 4, type: CustomFieldType.TEXT },
        { key: 'tenants.dob', option: 'dob', label: 'Date of Birth', span: 4, type: CustomFieldType.DATE }
      ],
      [
        // 3 + 9, not 4 + 8: every declared width has to be one v1 can express (`LEGACY_SPANS`), or the
        // firm's builder — which snaps to those widths — blocks Publish on a width the product chose.
        { key: 'tenants.ssn', option: 'ssn', label: 'SSN', span: 3, type: CustomFieldType.TEXT },
        {
          key: 'tenants.is_not_active_military_member',
          option: 'is_not_active_military_member',
          label: 'Not an active military member',
          span: 9,
          type: CustomFieldType.BOOLEAN
        }
      ]
    ]
  },
  {
    /*
     * Everything else the tenant step asks.
     *
     * v1 renders these inside Tenant Information; they are a section of their own here because none of
     * them is about who the tenants are — they are why the tenancy is ending and what has been declared
     * about it.
     */
    key: IntakeFormSections.EVICTION_REASON,
    title: 'Eviction Reason',
    note: 'Asked on the tenant step. The list of reasons itself is configured on the form, not here.',
    editable: true,
    rows: [
      [
        {
          key: 'eviction_reason',
          label: 'Eviction Reason',
          span: 3,
          required: true,
          locked: true,
          /*
           * A radio group, unless the firm asked for checkboxes.
           *
           * Legacy null / SELECT / RADIO is a radio group and MULTI_SELECT is checkboxes — the same
           * reading as `evictionReasonFieldFromOptions`. The cell type here is only the product
           * default; `toDefinitions` replaces it from the firm's option so a checkbox firm is not
           * drawn as a single select. The list itself is the wizard's default; a firm's own
           * `options['eviction_reason'].options` wins.
           */
          type: CustomFieldType.RADIO,
          options: [...EVICTION_REASONS_CLAIMS_MAP.keys()]
        },
        { key: 'reason', label: 'Other Reason Description', span: 9, type: CustomFieldType.TEXT }
      ],
      [
        {
          key: 'not_active_military',
          label: 'Tenants are not active military',
          span: LAYOUT_COLUMNS,
          type: CustomFieldType.BOOLEAN
        }
      ],
      [
        {
          key: 'applied_for_assistance',
          label: 'Applied for rental assistance',
          span: LAYOUT_COLUMNS,
          type: CustomFieldType.BOOLEAN
        }
      ]
    ]
  },
  {
    /*
     * The property step's own questions.
     *
     * `eviction-property-section.component.ts` is hand-written markup gated on options, exactly like the
     * tenant step — so the address, the property name and the property type were invisible to the
     * layout, and the canvas showed a Property section containing nothing but the firm's own custom
     * fields. Declared here for the same reason page one is.
     *
     * Merged rather than replacing: this section is also where a firm's property fields live.
     */
    key: IntakeFormSections.PROPERTY,
    title: 'Property',
    note:
      'The address is asked as one question — the autocomplete fills the rest of it, and the county ' +
      'comes from the ZIP. Which unit types it offers is yours to set.',
    editable: true,
    merge: true,
    rows: [
      [
        { key: 'property_name', label: 'Property Name', span: 6, type: CustomFieldType.TEXT },
        {
          /*
           * A radio in the wizard, and the three choices it hard-codes. A firm that had switched it off
           * in v1 does not get it placed — `show` becomes presence here as everywhere else — and it
           * comes back the ordinary way, from Add field.
           */
          key: 'property_type',
          label: 'Property Type',
          span: 6,
          type: CustomFieldType.RADIO,
          options: ['Conventional', 'Public Housing', 'Section 8'],
          // In a row, as v1 drew it. Three short choices stacked read as three questions.
          choiceLayout: ChoiceLayout.HORIZONTAL
        }
      ],
      [
        {
          /*
           * One question with seven parts, and the only one of them a firm can change is which unit
           * types the list offers. The autocomplete writes street, city, state and ZIP from a single
           * selection and the county is resolved from the ZIP, so a part placed on its own would be a
           * control the wizard overwrites.
           */
          key: 'property_address',
          label: COMPOSITE_LABELS.ADDRESS,
          span: LAYOUT_COLUMNS,
          required: true,
          always: true,
          locked: true,
          composite: 'ADDRESS'
        }
      ]
    ]
  },
  {
    /*
     * The claims step, as `claims-section.component.ts` draws it.
     *
     * Every question here is option-gated markup in v1 — the stored section holds only the firm's own
     * fields (`built_in_fields` is empty on every live form) — so until it was declared, v2 asked a
     * client for nothing but those and filed a case with no amount owed. v1 chose between one Total
     * Due and the itemised amounts with `options.claims.type`; here the choice is which cells are
     * placed, and the total is typed or computed accordingly.
     *
     * Civil recovery fees, interest and the interest start date have no input in v1 either and are not
     * declared; a stored value still rides through the edit-claims dialog, which is not this form.
     */
    key: IntakeFormSections.CLAIMS,
    title: 'Claims',
    note:
      'What the tenant owes. Ask for one Total Owed, or place the amounts that make it up and the ' +
      'total is worked out as they are entered. Rent Owed is asked as one amount or month by month — ' +
      'place one or the other.',
    editable: true,
    merge: true,
    rows: [
      [
        {
          key: 'past_due_rent',
          itemised: true,
          label: 'Rent Owed',
          span: 4,
          defaultShow: false,
          hiddenBy: 'past_due_rent_history',
          type: CustomFieldType.DECIMAL,
          monetary: true,
          include_in_total: true
        },
        {
          key: 'late_fees',
          itemised: true,
          label: 'Late Fee',
          span: 4,
          defaultShow: false,
          type: CustomFieldType.DECIMAL,
          monetary: true,
          include_in_total: true
        },
        {
          key: 'delinquency_length',
          itemised: true,
          label: 'Delinquency Length',
          span: 4,
          defaultShow: false,
          type: CustomFieldType.TEXT,
          hint: `Ex. Jan 1, ${new Date().getFullYear()} - Mar 1, ${new Date().getFullYear()}`
        }
      ],
      [
        {
          // Month by month, each row a month, a year and an amount; the sum is Rent Owed.
          key: 'past_due_rent_history',
          itemised: true,
          label: 'Rent Owed, by month',
          span: LAYOUT_COLUMNS,
          defaultShow: false,
          required: true,
          fixedRequired: true,
          type: CustomFieldType.RENT_HISTORY,
          include_in_total: true
        }
      ],
      [
        {
          key: 'damages',
          itemised: true,
          label: 'Damages',
          span: 3,
          defaultShow: false,
          type: CustomFieldType.DECIMAL,
          monetary: true,
          include_in_total: true
        },
        {
          key: 'damages_description',
          itemised: true,
          label: 'Damage Description',
          span: 9,
          defaultShow: false,
          type: CustomFieldType.TEXT
        }
      ],
      [
        {
          key: 'attorney_fee',
          itemised: true,
          label: 'Attorney Fee',
          span: 4,
          defaultShow: false,
          type: CustomFieldType.DECIMAL,
          monetary: true,
          include_in_total: true
        },
        {
          key: 'costs',
          itemised: true,
          label: 'Costs',
          span: 4,
          defaultShow: false,
          type: CustomFieldType.DECIMAL,
          monetary: true,
          include_in_total: true
        },
        {
          key: 'partial_payments',
          itemised: true,
          label: 'Partial Payments',
          span: 4,
          defaultShow: false,
          type: CustomFieldType.DECIMAL,
          monetary: true,
          subtract_from_total: true
        }
      ],
      [
        {
          /*
           * What the tenant owes, and the one amount every case carries.
           *
           * Asked outright when it is the only amount on the section (v1's "simple" claims), and worked
           * out — the amounts above it and any firm field flagged `include_in_total`, less partial
           * payments — the moment one of those is placed (v1's "Total Amount" line); `toDefinitions`
           * makes that switch. Refused at zero either way. Placed last so it reads as the sum of what
           * is above it, and locked because nothing a firm removes here would stop the case carrying it.
           */
          key: 'tenant_total_owed',
          label: 'Total Owed',
          span: 4,
          required: true,
          always: true,
          locked: true,
          type: CustomFieldType.DECIMAL,
          monetary: true,
          positive: true,
          section_total: true
        }
      ]
    ]
  },
  {
    /*
     * The lease step, as `lease-terms-section.component.ts` draws it — option-gated markup, like Claims.
     *
     * Due Day and Rent Frequency start as "1st" and "Month" because v1's form does, and Lease Terms
     * offers the firm's list where it set one. The notice date is not asked while the lease waives
     * notice; that is the wizard's rule, not a placement, since it depends on an answer.
     */
    key: IntakeFormSections.LEASE_TERMS,
    title: 'Lease Terms',
    note:
      'What the lease says. Lease Terms offers your own choices where you have set them; the notice ' +
      'date is skipped when the lease contains a waiver of notice.',
    editable: true,
    merge: true,
    rows: [
      [
        {
          key: 'lease_rent_amount',
          label: 'Lease Monthly Rent',
          span: 4,
          defaultShow: false,
          type: CustomFieldType.DECIMAL,
          monetary: true
        },
        {
          key: 'lease_rent_due_day',
          label: 'Due Day',
          span: 4,
          defaultShow: false,
          type: CustomFieldType.TEXT,
          initial: '1st'
        },
        {
          key: 'lease_rent_frequency',
          label: 'Rent Frequency',
          span: 4,
          defaultShow: false,
          type: CustomFieldType.TEXT,
          initial: 'Month'
        }
      ],
      [
        {
          key: 'security_deposit',
          label: 'Security Deposit',
          span: 4,
          defaultShow: false,
          type: CustomFieldType.DECIMAL,
          monetary: true
        },
        {
          key: 'lease_terms',
          label: 'Lease Terms',
          span: 4,
          defaultShow: false,
          type: CustomFieldType.SELECT,
          options: ['Month to Month', 'Written Lease']
        },
        { key: 'move_in_date', label: 'Move-In Date', span: 4, defaultShow: false, type: CustomFieldType.DATE }
      ],
      [
        {
          key: 'waiver_of_notice',
          label: 'Lease contains a waiver of notice',
          span: 6,
          defaultShow: false,
          type: CustomFieldType.BOOLEAN,
          initial: false
        },
        { key: 'notice_date', label: 'Notice Date', span: 6, defaultShow: false, type: CustomFieldType.DATE }
      ]
    ]
  }
];

/** v1 skips the notice date while the lease waives notice. The wizard applies it; the host decides for whom. */
export const WAIVER_OF_NOTICE_KEY = 'waiver_of_notice';

export const NOTICE_DATE_KEY = 'notice_date';

/** A rent history's sum is Rent Owed: v1 wrote it there, and the total and the documents read it. */
export const RENT_HISTORY_KEY = 'past_due_rent_history';

export const PAST_DUE_RENT_KEY = 'past_due_rent';

/**
 * Why every field on page one is locked: they create the client's account, so the form cannot ask for
 * anything else in their place, and it cannot ask for them anywhere else.
 */
const PUBLIC_FORM_LOCK: PlacementLockReason = 'CLIENT_CREATION';


/** The order the fixed section map is laid out in today, and the pages they fall on. */
export const DEFAULT_PAGES: { id: string; name: string; publicOnly?: boolean; fixed?: boolean; sections: IntakeFormSections[] }[] = [
  {
    id: 'page-account',
    // Named for what it is to the person filling it in, and matching the live form's own first two
    // sections. "Your account" was invented here and corresponded to nothing on the real form.
    name: 'Getting started',
    // The public form asks the account questions because the filer has no account; the portal already
    // knows who they are and skips the page entirely.
    publicOnly: true,
    fixed: true,
    sections: [IntakeFormSections.USER, IntakeFormSections.ORGANIZATION]
  },
  {
    id: 'page-parties',
    name: 'Parties & property',
    // Eviction Reason sits under the tenants on today's form, not with the claims — it is a fact about
    // why this tenancy is ending, which is why it is asked where the tenancy is described.
    sections: [
      IntakeFormSections.PLAINTIFF,
      IntakeFormSections.TENANT,
      IntakeFormSections.EVICTION_REASON,
      IntakeFormSections.PROPERTY
    ]
  },
  {
    id: 'page-claims',
    // Claims and Lease Terms, as today's form groups them — the lease is what the claim is measured
    // against. Documents were never on this page.
    name: 'Claims & lease terms',
    sections: [IntakeFormSections.CLAIMS, IntakeFormSections.LEASE_TERMS]
  },
  {
    id: 'page-documents',
    name: 'Documents',
    /*
     * Documents only. Other Information and Internal Fields are deliberately not intake pages.
     *
     * Neither was ever a set of questions for a client. In v1, putting a field in one of them was the
     * only way to make it appear on the case sidebar, so they filled up with fields that exist to be
     * *seen by staff* rather than asked at intake. The sidebar has its own layout now, so the workaround
     * has somewhere real to go and the sections have no reason to be on the form.
     *
     * Dropping them from the pages is not destructive: `applyPages` only writes sections that appear on
     * a page, so their stored fields are left exactly as they are and stay available to the sidebar and
     * the registry.
     */
    sections: [IntakeFormSections.DOCUMENTS]
  }
];

/** True for a section whose questions the product owns — page one of the public form. */
export function isPublicFormSection(key: string): boolean {
  // Compared as strings: `key` is a section key off the document, which is an enum value only by
  // convention — a custom section's key is whatever its author typed.
  return PUBLIC_FORM_SECTIONS.some(section => String(section.key) === key);
}

/**
 * True for a declared section that also holds the firm's own fields.
 *
 * It is declared, so it is projected; but it is not wholly the product's, so it is written back like an
 * ordinary section. Storing only the four keys the product-owned branch keeps would drop a firm's custom
 * property fields on the first save.
 */
export function isMergedFormSection(key: string): boolean {
  return PUBLIC_FORM_SECTIONS.some(section => String(section.key) === key && section.merge === true);
}

/**
 * The fixed page-one section: the product's questions, arranged by whatever the firm has authored around
 * them.
 *
 * The **structure** is stored, the **fields** are not. So the saved rows decide where a heading sits
 * relative to the questions, while every field item is re-read from the declaration — which means a
 * heading dragged between two rows stays there, and nothing about the questions themselves can go stale
 * or be corrupted by an edit.
 *
 * Reconciled rather than trusted: a stored field the declaration no longer has is dropped, and a declared
 * field the stored rows have lost is put back in its canonical row. A form saved before a question was
 * added or renamed therefore repairs itself on read instead of quietly asking the wrong thing.
 */
export function publicFormSection(
  stored: IntakeFormSection | undefined,
  index: number,
  options: Record<string, IntakeOption> = {}
): LayoutSectionView {
  const declared = PUBLIC_FORM_SECTIONS[index];
  const declaredByKey = new Map(declared.rows.flat().map(cell => [cell.key, cell]));

  /*
   * v1's `show` becomes presence.
   *
   * There is no "hidden but configured" in v2 — a field is placed or it is offered in Add field — so the
   * option is read once here and never again. A cell the firm had switched off simply does not appear,
   * and comes back the ordinary way. `required` becomes the placement's own flag for the same reason.
   */
  const shown = (cell: DeclaredCell): boolean =>
    cell.always === true ||
    ((options[cell.option ?? cell.key]?.show ?? cell.defaultShow ?? true) &&
      !(cell.hiddenBy && options[cell.hiddenBy]?.show === true) &&
      !(cell.itemised && claimsAskedAsOneTotal(options)));

  const requiredFor = (cell: DeclaredCell): boolean =>
    cell.fixedRequired === true || (options[cell.option ?? cell.key]?.required ?? cell.required ?? false);

  /*
   * The expanded hint, which v1 renders as "Hover for more info" beside a claims or tenant field.
   *
   * Two stores hold the same sentence. `section.localization['<key>_hint']` is the one the wizard
   * reads and the localization editor writes; `options[key].hint` is the older one it was seeded from,
   * and no component reads it any more. Both are consulted, newest first, so nothing a firm typed is
   * left behind — 39 options and a dozen localizations carry one in a sample estate.
   */
  const hintFor = (cell: DeclaredCell): string | null =>
    stored?.localization?.[`${cell.key}_hint`] || options[cell.option ?? cell.key]?.hint || cell.hint || null;

  const cellToItem = (cell: DeclaredCell) => ({
    kind: cell.composite ? ('COMPOSITE' as const) : ('FIELD' as const),
    field_key: cell.key,
    composite_type: cell.composite ?? null,
    span: cell.span,
    hint: hintFor(cell),
    /*
     * The link beside a question, which v1 offers on the rental-assistance declaration and renders as an
     * info icon. Eighteen forms in a sample estate set one, most of them to their state's ERAP page.
     */
    more_info_link: options[cell.option ?? cell.key]?.more_info_link || null,
    /*
     * The label the component actually renders, carried on the placement.
     *
     * Declared and then not used, these fell back to the registry definition — so a firm that had
     * relabelled `company_name` saw Business Information ask for "Plaintiff Name". The definition is the
     * wrong source here: page one is markup, and what it prints is not the registry's to change.
     *
     * It rides on `label_override` because that is where a per-surface label lives, but the tile does
     * not mark a locked placement as overridden — nobody chose this, it is simply what the page says.
     *
     * A firm's own wording wins over the declaration's. v1 lets one be set per question and renders it
     * on the rental-assistance declaration; eighteen forms have renamed that one, and taking the
     * declared label unconditionally put the product's words back in their mouths.
     */
    label_override: options[cell.option ?? cell.key]?.label_override || cell.label,
    required_intake: requiredFor(cell),
    // An editable section locks only the cells v1 always renders; a fixed one locks everything.
    lock_reason: declared.editable ? (cell.locked ? 'PRODUCT_OWNED' : null) : PUBLIC_FORM_LOCK
  });

  const canonical = () =>
    declared.rows
      .map((cells, rowIndex) => ({
        id: `${String(declared.key).toLowerCase()}-row-${rowIndex}`,
        items: cells.filter(shown).map(cellToItem)
      }))
      .filter(row => row.items.length);

  /*
   * A composite owns its parts, so a part can never also be a placement of its own.
   *
   * This is a migration guard, not a theoretical one. A firm that has been through the v2 migration has
   * `property_street_address` and `property_city` in its stored rows, because until the address was
   * declared they were ordinary fields — and passing them through beside the block would put two
   * editors on one storage key, which is the thing the composite exists to prevent.
   */
  const ownedByComposite = (item: { field_key?: string | null }): boolean =>
    !!item.field_key && COMPOSITE_PART_KEYS.has(item.field_key);

  /*
   * The firm's own placement in a merged section, as an ordinary section would have projected it: its
   * stored hint or the section's `localization['<key>_hint']`, and the product's lock where the key
   * has one (`plaintiff_name`). Without this a field that moved from an ordinary section into Claims
   * would arrive without either.
   */
  const ownItem = (item: LayoutItem): LayoutItem => ({
    ...item,
    hint: item.hint ?? (item.field_key ? (stored?.localization?.[`${item.field_key}_hint`] ?? null) : null),
    lock_reason: item.lock_reason ?? lockReasonFor(item.field_key)
  });
  // Never a question (`tags`), so never drawn — the same rule an ordinary section applies.
  const ownRows = (rows: LayoutRow[]): LayoutRow[] =>
    rows
      .map(row => ({ ...row, items: row.items.filter(item => !item.field_key || isIntakePlaceable(item.field_key)).map(ownItem) }))
      .filter(row => row.items.length);

  const storedRows = stored?.rows ?? [];
  if (!storedRows.length) {
    /*
     * No stored rows.
     *
     * On a wholly product-owned section that is the whole answer. On a merged one it is the *legacy*
     * case — a firm that has never been migrated — and its own property fields are still in
     * `fields` / `built_in_fields`, never having been packed into rows. Returning the declaration alone
     * would show that firm a Property section with their gate code and property ID missing, which is
     * exactly what the un-migrated canvas exists to be honest about.
     */
    if (!declared.merge) {
      return view(canonical());
    }
    const own = migrateFieldsToRows(
      dedupeByKey([...(stored?.built_in_fields ?? []), ...(stored?.fields ?? [])]).filter(
        field => !declaredByKey.has(field.field) && !COMPOSITE_PART_KEYS.has(field.field)
      )
    );
    return view([...canonical(), ...ownRows(own)]);
  }

  const seen = new Set<string>();
  const rows = storedRows
    .map(row => ({
      id: row.id,
      items: row.items
        .map(item => {
          if (!item.field_key) {
            return item;
          }
          const cell = declaredByKey.get(item.field_key);
          if (!cell) {
            /*
             * Not one of the wizard's questions. On a merged section that is the firm's own field and it
             * passes through untouched; anywhere else it is a stale write against a key the declaration
             * no longer has, and keeping it would put a question on the form that nothing asks.
             *
             * Unless the composite now owns it — see `ownedByComposite`. Dropping it is not a loss: the
             * block places the same storage key, so the question is still asked and the data still lands
             * in the same place.
             */
            return declared.merge && !ownedByComposite(item) && isIntakePlaceable(item.field_key) ? ownItem(item) : null;
          }
          if (seen.has(cell.key)) {
            return null;
          }
          seen.add(cell.key);
          /*
           * Label, type and lock always come from the declaration — they are the wizard's. Width and
           * requiredness come from the stored placement where there is one, because on an editable
           * section those are the firm's edits, and re-deriving them from the option would undo a
           * resize or a Required toggle on the next load.
           */
          /*
           * A locked cell keeps the declared width too, not just the declared label and type.
           *
           * It offers no resize handle, so a stored width for one cannot be a firm's edit — it is drift
           * from a seeder or a migrator. The seeded eviction reason had it at 12 beside a 12-wide Other
           * Reason Description, which is a 24-column row a firm could not fix from the canvas: the one
           * placement that would have to give way is the locked one.
           */
          return declared.editable
            ? {
              ...cellToItem(cell),
              span: cell.locked ? cell.span : (item.span ?? cell.span),
              required_intake: item.required_intake ?? requiredFor(cell),
              // What the firm wrote on the placement since, over what the declaration reads off v1.
              hint: item.hint ?? hintFor(cell),
              more_info_link: item.more_info_link ?? cellToItem(cell).more_info_link
            }
            : cellToItem(cell);
        })
        .filter((item): item is NonNullable<typeof item> => !!item)
    }))
    .filter(row => row.items.length);

  /*
   * Anything the stored rows lost comes back.
   *
   * On a fixed section that is every declared cell — a question added since the save. On an editable one
   * a missing cell is usually a removal the firm made, so only the **locked** ones return: those cannot
   * be removed, so their absence is never a decision.
   */
  /*
   * On a merged section, absence cannot mean "removed" until a declared cell has been stored at least
   * once. No form has ever held the property questions — they were markup — so on first read every shown
   * cell is placed. Once one of them is in the stored rows the section has been authored in v2 terms and
   * the ordinary rule takes over: only the locked ones come back.
   */
  const neverAuthored = declared.merge === true && seen.size === 0;

  const missing = canonical()
    .map(row => ({
      ...row,
      items: row.items.filter(
        item =>
          !seen.has(item.field_key) &&
          (!declared.editable || neverAuthored || declaredByKey.get(item.field_key)?.locked)
      )
    }))
    .filter(row => row.items.length);

  /*
   * Stored rows that resolve to nothing are a mismatch, not an empty section.
   *
   * Every editable declared section has at least one locked cell, so it cannot legitimately end up with
   * no declared fields. Rows written against keys this build no longer knows — a rename, or a restore
   * from an older document — would otherwise render the section blank with no way back.
   *
   * Guarded on the stored rows having *had* field keys: a section holding nothing but an authored
   * heading has no declared fields either, and resetting that would throw the heading away.
   */
  const storedFieldKeys = storedRows.flatMap(row => row.items).filter(item => !!item.field_key);
  if (!declared.merge && storedFieldKeys.length && !seen.size) {
    return view(canonical());
  }

  /*
   * The wizard's questions lead a merged section. Appending them would put the address — the thing the
   * property step exists to ask — underneath whatever custom fields the firm happens to have added.
   */
  return view(declared.merge ? [...missing, ...rows] : [...rows, ...missing]);

  function view(built: { id: string; items: unknown[] }[]): LayoutSectionView {
    return {
      key: String(declared.key),
      title: declared.title,
      rows: repackOverfullRows(built as LayoutSectionView['rows']),
      firm_only: false,
      fields_owned_by_product: !declared.editable,
      // A merged section shows keys: most of what it holds is the firm's own fields, and the key is how
      // they are identified everywhere else. Only a wholly product-owned page has nothing to identify.
      hide_field_keys: !declared.merge,
      locked_reason: declared.note,
      closed_to_moves: !declared.merge
    };
  }
}
